import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ThemeContext } from './context';
import {
  ThemeContextType,
  ThemeMode,
  UseThemeReturn,
  SUPPORTED_THEMES
} from '@/types/theme';

const STORAGE_KEY = 'pf2e_theme_preference';
const THEME_TRANSITION_MS = 1040;

interface ThemeProviderProps {
  children: React.ReactNode;
  defaultTheme?: ThemeMode;
}

export function ThemeProvider({
  children,
  defaultTheme = 'auto'
}: ThemeProviderProps) {
  const [theme, setThemeState] = useState<ThemeMode>(() => {
    const stored = localStorage.getItem(STORAGE_KEY) as ThemeMode | null;
    if (stored && SUPPORTED_THEMES.some(t => t.mode === stored)) {
      return stored;
    }
    return defaultTheme;
  });

  const [isDark, setIsDark] = useState(false);
  const hasAppliedThemeRef = useRef(false);
  const transitionTimeoutRef = useRef<number | null>(null);

  // Определение тёмной темы
  useEffect(() => {
    let mounted = true;

    function applyTheme() {
      const root = document.documentElement;
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      let shouldBeDark = false;

      if (theme === 'auto') {
        shouldBeDark = mediaQuery.matches;
      } else if (theme === 'dark') {
        shouldBeDark = true;
      }

      if (mounted) {
        const shouldAnimate = hasAppliedThemeRef.current && !reduceMotion.matches;
        if (shouldAnimate) {
          if (transitionTimeoutRef.current) {
            window.clearTimeout(transitionTimeoutRef.current);
          }
          root.dataset.themeTransition = 'true';
          void root.offsetWidth;
        }

        setIsDark(shouldBeDark);

        if (shouldBeDark) {
          root.classList.add('dark');
          root.classList.remove('light');
        } else {
          root.classList.add('light');
          root.classList.remove('dark');
        }

        hasAppliedThemeRef.current = true;

        if (shouldAnimate) {
          transitionTimeoutRef.current = window.setTimeout(() => {
            root.removeAttribute('data-theme-transition');
            transitionTimeoutRef.current = null;
          }, THEME_TRANSITION_MS + 120);
        }
      }
    }

    applyTheme();

    // Слушатель для системной темы (когда выбран 'auto')
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      if (theme === 'auto') {
        applyTheme();
      }
    };

    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mounted = false;
      if (transitionTimeoutRef.current) {
        window.clearTimeout(transitionTimeoutRef.current);
        transitionTimeoutRef.current = null;
      }
      document.documentElement.removeAttribute('data-theme-transition');
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, [theme]);

  // Публичная функция смены темы
  const setTheme = useCallback((newTheme: ThemeMode) => {
    if (SUPPORTED_THEMES.some(t => t.mode === newTheme)) {
      setThemeState(newTheme);
      localStorage.setItem(STORAGE_KEY, newTheme);
    }
  }, []);

  const value: ThemeContextType = useMemo(() => ({
    theme,
    setTheme,
    isDark,
    isLoading: false
  }), [theme, setTheme, isDark]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

// Хук для использования темы
// eslint-disable-next-line react-refresh/only-export-components
export function useTheme(): UseThemeReturn {
  const context = React.useContext(ThemeContext);

  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }

  const { theme, setTheme, isDark, isLoading } = context;

  const currentTheme = useMemo(
    () => SUPPORTED_THEMES.find(t => t.mode === theme),
    [theme]
  );

  return {
    theme,
    setTheme,
    currentTheme,
    supportedThemes: SUPPORTED_THEMES,
    isDark,
    isLoading
  };
}
